import {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux'
import {Message} from "../../components/Message";
import {useNavigate} from 'react-router-dom';
import {Spinner} from "flowbite-react";
import {useOrganizationList} from "@clerk/clerk-react";
import {
  HiChevronDown,
  HiFilter,
  HiBell,
  HiCheckCircle,
  HiMail,
  HiMailOpen,
} from "react-icons/hi";

import {useNotificationService} from '../../services';
import {useJwt} from "../../helpers/common";
import {setNotifications, addSeenNotification, clearReadNotificationState, setReadBatchTimeout} from '../../redux/slices/notification';
import MessageSkeleton from '../../components/Message/MessageSkeleton';

const READ_THRESHOLD = 5; // Send to server when 5+ notifications are seen
const READ_TIMEOUT = 5000; // Send remaining notifications after 10 seconds

const Activity = () => {
  const [filterType, setFilterType] = useState('all');
  const [filteredNotifications, setFilteredNotifications] = useState<any[]>([]);
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  const notificationRefs = useRef<HTMLElement[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const readTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fetchOnceRef = useRef<boolean>(false);
  const JWTdata:any = useJwt()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const {setActive } = useOrganizationList();
  const { deleteNotification, markNotificationsAsRead, fetchNotifications } = useNotificationService();

  const {
    data: notificationData,
    loading,
    deleteLoading,
    seenNotificationIds,
    processedNotificationIds,
    readBatchTimeout
  } = useSelector((state: any) => state.notification);

  const [visibleCount, setVisibleCount] = useState(10);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Process seen notifications with batching logic
  const processReadNotifications = async () => {
    const unprocessedIds = seenNotificationIds.filter((id: number) =>
      !processedNotificationIds.includes(id)
    );

    if (unprocessedIds.length >= READ_THRESHOLD) {
      // Send immediately when threshold is reached
      try {
        await markNotificationsAsRead(unprocessedIds);
        // Clear any existing timeout since we're sending now
        if (readTimeoutRef.current) {
          clearTimeout(readTimeoutRef.current);
          readTimeoutRef.current = null;
        }
        dispatch(setReadBatchTimeout(false));
      } catch (error) {
        console.error('Failed to mark notifications as read:', error);
      }
    } else if (unprocessedIds.length > 0 && !readBatchTimeout) {
      // Set timeout for smaller batches
      dispatch(setReadBatchTimeout(true));

      readTimeoutRef.current = setTimeout(async () => {
        try {
          // Get current unprocessed IDs in case more notifications were seen
          const currentUnprocessedIds = seenNotificationIds.filter((id: number) =>
            !processedNotificationIds.includes(id)
          );
          if (currentUnprocessedIds.length > 0) {
            await markNotificationsAsRead(currentUnprocessedIds);
          }
        } catch (error) {
          console.error('Failed to mark notifications as read (timeout):', error);
        } finally {
          dispatch(setReadBatchTimeout(false));
          readTimeoutRef.current = null;
        }
      }, READ_TIMEOUT);
    }
  };

  useEffect(() => {
    if (seenNotificationIds.length > 0) {
      processReadNotifications();
    }
  }, [seenNotificationIds, processedNotificationIds, readBatchTimeout]);

  useEffect(() => {
    dispatch(clearReadNotificationState());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      if (readTimeoutRef.current) {
        clearTimeout(readTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current || notificationData.length === 0) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Extract notification ID from the element's data attribute or index
          const notificationIndex = parseInt(entry.target.getAttribute('data-notification-index') || '0');
          const notification = filteredNotifications[notificationIndex];

          if (notification && !notification.read &&
            !seenNotificationIds.includes(notification.id) &&
            !processedNotificationIds.includes(notification.id)) {
            // Add to seen notifications via Redux
            dispatch(addSeenNotification(notification.id));
          }
        }
      });
    }, {
      root: containerRef.current,
      threshold: 0.75,
    });

    const rafId = requestAnimationFrame(() => {
      notificationRefs.current.forEach((el) => {
        if (el) observer.observe(el);
      });
    });

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [notificationData, filteredNotifications, seenNotificationIds, processedNotificationIds, dispatch]);

  useEffect(() => {
    // Reset on mount so navigating back to Inbox always re-fetches
    fetchOnceRef.current = false;
    setInitialLoading(true);
  }, []);

  useEffect(() => {
    if (JWTdata?.org_id && !fetchOnceRef.current) {
      fetchOnceRef.current = true;
      fetchNotifications(JWTdata.org_id);
    }
  }, [JWTdata?.org_id]);

  // Turn off initial loading once the first fetch completes
  useEffect(() => {
    if (fetchOnceRef.current && !loading) {
      setInitialLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    const t = setTimeout(async (JWT) => {
      const {org_id = "",org_list=[]} = JWT
      if (!org_id ) {
        if(Object.keys(org_list).length){
          const [firstOrgId=''] = Object.keys(org_list)
          if(firstOrgId && typeof setActive == "function"){
            await setActive({ organization: firstOrgId })
          } else {
            window.location.href = "/signup"
          }
        } else {
          window.location.href = "/signup"
        }
      }
    }, 4000,JWTdata);
    return () => {
      clearTimeout(t)
    }
  }, [JWTdata]);

  const handleClick = async(data:any)=> {
    if(!data.read){
      // await readNotification(data.id)
    }
    if(data.type === "roadmap"){
      window.location.href = `/roadmaps/${data.entity_id}`;
    } else {
      window.location.href = `/projects/${data.entity_id}`;
    }
  }
  const handleDeleteClick = async(id:any)=> {
    if(id){
      await deleteNotification(id)
      if(!loading){
        const data = notificationData.filter((noti:any)=>noti.id !==id)
        dispatch(setNotifications(data))
      }
    }
  }

  const handleMarkAllAsRead = async () => {
    if (isMarkingAll) return;
    const unreadIds = notificationData
      .filter((n: any) => !n.read)
      .map((n: any) => n.id);
    if (unreadIds.length === 0) return;
    setIsMarkingAll(true);
    try {
      await markNotificationsAsRead(unreadIds);
      const updated = notificationData.map((n: any) =>
        unreadIds.includes(n.id) ? { ...n, read: true } : n
      );
      dispatch(setNotifications(updated));
      dispatch(clearReadNotificationState());
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    } finally {
      setIsMarkingAll(false);
    }
  }

  const filterOptions = [
    { label: 'All', value: 'all' },
    { label: 'Last 6 Months', value: '6months' },
    { label: 'Last 3 Months', value: '3months' },
    { label: 'Last 30 days', value: '1month' },
    { label: 'Custom Range', value: 'custom' }
  ];

  const filterNotifications = (type: string) => {
    let filtered = [...notificationData];

    if (type !== "all") {
      const currentDate = new Date();
      let startDate = new Date();
      let endDate = new Date();

      switch (type) {
        case "6months":
          startDate.setMonth(currentDate.getMonth() - 6);
          break;
        case "3months":
          startDate.setMonth(currentDate.getMonth() - 3);
          break;
        case "1month":
          startDate.setMonth(currentDate.getMonth() - 1);
          break;
        case "custom":
          if (customDateRange.startDate && customDateRange.endDate) {
            startDate = new Date(customDateRange.startDate);
            endDate = new Date(customDateRange.endDate);
            endDate.setHours(23, 59, 59, 999);
          } else {
            setFilteredNotifications(
              showOnlyUnread ? filtered.filter((n) => !n.read) : filtered
            );
            return;
          }
          break;
        default:
          break;
      }

      filtered = filtered.filter((notification: any) => {
        const notificationDate = new Date(notification.created_at);
        if (type === "custom") {
          return notificationDate >= startDate && notificationDate <= endDate;
        }
        return notificationDate >= startDate;
      });
    }

    if (showOnlyUnread) {
      filtered = filtered.filter((n) => !n.read);
    }

    setFilteredNotifications(filtered);
  };

  useEffect(() => {
    filterNotifications(filterType);
  }, [filterType, customDateRange, notificationData, showOnlyUnread]);

  // Reset visibleCount when filters change
  useEffect(() => {
    setVisibleCount(10);
  }, [filterType, customDateRange, notificationData, showOnlyUnread]);

  // Infinite scroll: load more when bottom is reached
  useEffect(() => {
    if (!loadMoreRef.current) return;
    if (filteredNotifications.length <= visibleCount) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingMore) {
          setIsFetchingMore(true);
          setTimeout(() => {
            setVisibleCount((prev) => Math.min(prev + 10, filteredNotifications.length));
            setIsFetchingMore(false);
          }, 500); // Simulate loading delay
        }
      },
      { threshold: 1 }
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [filteredNotifications, visibleCount, isFetchingMore]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdown = document.getElementById("filter-dropdown");
      const dropdownButton = document.getElementById("filter-dropdown-button");
      const target = event.target as HTMLElement;

      if (
        dropdown &&
        !dropdown.contains(target) &&
        dropdownButton &&
        !dropdownButton.contains(target)
      ) {
        dropdown.classList.add("hidden");
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const getFilteredStats = () => {
    const totalNotifications = filteredNotifications.length;
    const unreadCount = filteredNotifications.filter((n) => !n.read).length;
    return { totalNotifications, unreadCount };
  };

  const stats = getFilteredStats();

 

  return (
    <div className="min-h-screen w-full bg-tertiary-light dark:bg-tertiary-dark flex flex-col h-screen">
      {/* Enhanced Header Section - Now Sticky */}
      <div className="bg-white dark:bg-secondary-dark shadow-lg border-b rounded-b-xl border-light-border-subtle dark:border-gray-700 flex-shrink-0 sticky top-0 z-10">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            {/* Title Section */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center w-12 h-12 bg-light-status-info-bg dark:bg-blue-900 rounded-xl">
                  <HiBell className="w-6 h-6 text-light-status-info-text dark:text-blue-400" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-light-text-primary dark:text-white">
                    Activity Center
                  </h1>
                  <p className="text-sm text-light-text-secondary dark:text-gray-400 mt-1">
                    Stay updated with your latest notifications and activities
                  </p>
                </div>
              </div>

              {/* Status Indicators */}
              <div className="flex items-center space-x-3">
                {(deleteLoading || !JWTdata.org_id || loading) && (
                  <div className="flex items-center space-x-2 px-3 py-1.5 bg-light-status-info-bg dark:bg-blue-900/30 rounded-full">
                    <Spinner size="sm" />
                    <span className="text-sm font-medium text-light-status-info-text dark:text-blue-300">
                      Updating...
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Controls Section */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Left Section - Pill Toggle & Stats */}
              <div className=" flex items-center space-x-6">
                {/* Pill Toggle for All/Unread */}
                <div className="flex items-center bg-light-surfaceSecondary dark:bg-gray-700 rounded-full p-1">
                  <button
                    onClick={() => setShowOnlyUnread(false)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                      !showOnlyUnread
                        ? "bg-light-surface dark:bg-tertiary-dark text-light-text-primary dark:text-white shadow-sm"
                        : "text-light-text-secondary dark:text-gray-400 hover:text-light-text-primary dark:hover:text-white"
                    }`}
                  >
                    <HiMailOpen className="w-4 h-4" />
                    <span>All</span>
                    {(loading || initialLoading) ? (
                      <span className="w-6 h-4 bg-light-surfaceSecondary dark:bg-gray-600 rounded animate-pulse" />
                    ) : (
                      <span className="bg-light-surfaceSecondary dark:bg-gray-600 px-2 py-0.5 rounded-full text-xs">
                        {notificationData.length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setShowOnlyUnread(true)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                      showOnlyUnread
                        ? "bg-light-surface dark:bg-tertiary-dark text-light-text-primary dark:text-white shadow-sm"
                        : "text-light-text-secondary dark:text-gray-400 hover:text-light-text-primary dark:hover:text-white"
                    }`}
                  >
                    <HiMail className="w-4 h-4" />
                    <span>Unread</span>
                    {(loading || initialLoading) ? (
                      <span className="w-6 h-4 bg-light-surfaceSecondary dark:bg-gray-600 rounded animate-pulse" />
                    ) : notificationData.filter((n: any) => !n.read).length > 0 && (
                      <span className="bg-light-status-error-bg text-light-status-error-text px-2 py-0.5 rounded-full text-xs">
                        {notificationData.filter((n: any) => !n.read).length}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Right Section - Filters */}
              <div className="flex items-center space-x-3">
                {!initialLoading && notificationData.some((n: any) => !n.read) && (
                  <button
                    onClick={handleMarkAllAsRead}
                    disabled={isMarkingAll || loading}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white bg-primary-light dark:bg-primary-dark hover:bg-primary-hover focus:ring-2 focus:ring-primary-light focus:border-primary-light disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isMarkingAll ? (
                      <Spinner size="sm" />
                    ) : (
                      <HiCheckCircle className="w-4 h-4" />
                    )}
                    <span>Mark all as read</span>
                  </button>
                )}
                {/* Custom Date Range */}
                {filterType === "custom" && (
                  <div className="flex items-center space-x-2 px-3 py-2 bg-light-surfaceSecondary dark:bg-gray-700 rounded-lg">
                    <input
                      type="date"
                      className="px-2 py-1 text-sm bg-light-surface dark:bg-tertiary-dark border-light-border-default dark:border-gray-600 rounded-md text-light-text-primary dark:text-white focus:ring-primary-light focus:border-primary-light"
                      value={customDateRange.startDate}
                      onChange={(e) => {
                        setCustomDateRange((prev) => ({
                          ...prev,
                          startDate: e.target.value,
                          endDate:
                            prev.endDate &&
                            new Date(prev.endDate) < new Date(e.target.value)
                              ? ""
                              : prev.endDate,
                        }));
                      }}
                    />
                    <span className="text-sm font-medium text-light-text-secondary dark:text-gray-300">to</span>
                    <input
                      type="date"
                      className="px-2 py-1 text-sm bg-light-surface dark:bg-tertiary-dark border-light-border-default dark:border-gray-600 rounded-md text-light-text-primary dark:text-white focus:ring-primary-light focus:border-primary-light"
                      value={customDateRange.endDate}
                      min={customDateRange.startDate}
                      onChange={(e) => {
                        setCustomDateRange((prev) => ({
                          ...prev,
                          endDate: e.target.value,
                        }));
                      }}
                    />
                  </div>
                )}

                {/* Filter Dropdown with Fixed Width */}
                <div className="relative">
                  <button
                    id="filter-dropdown-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const dropdown = document.getElementById("filter-dropdown");
                      if (dropdown) {
                        dropdown.classList.toggle("hidden");
                      }
                    }}
                    className="inline-flex items-center justify-between w-40 px-4 py-2 text-sm font-medium text-light-text-primary dark:text-gray-300 bg-light-surface dark:bg-tertiary-dark border border-light-border-default dark:border-gray-600 rounded-lg hover:bg-light-interactive-hover dark:hover:bg-gray-700 focus:ring-2 focus:ring-primary-light focus:border-primary-light transition-colors duration-200"
                  >
                    <div className="flex items-center space-x-2">
                      <HiFilter className="w-4 h-4" />
                      <span className="truncate">
                        {filterOptions.find((opt) => opt.value === filterType)?.label}
                      </span>
                    </div>
                    <HiChevronDown className="w-4 h-4 flex-shrink-0" />
                  </button>

                  <div
                    id="filter-dropdown"
                    className="hidden absolute right-0 top-full z-50 mt-2 w-48 bg-light-surface dark:bg-tertiary-dark rounded-lg shadow-lg border border-light-border-default dark:border-gray-600 overflow-hidden"
                  >
                    <div className="py-1">
                      {filterOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setFilterType(option.value);
                            const dropdown = document.getElementById("filter-dropdown");
                            if (dropdown) {
                              dropdown.classList.add("hidden");
                            }
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-light-interactive-hover dark:hover:bg-gray-700 transition-colors duration-150 ${
                            filterType === option.value
                              ? "bg-light-status-info-bg dark:bg-blue-900/30 text-light-status-info-text dark:text-blue-300 font-medium"
                              : "text-light-text-primary dark:text-gray-300"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full Height Notifications List */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto"
      >
        {/* Quick Stats */}
        <div className="hidden px-8 pt-4 -mb-2 sm:flex items-center space-x-4 text-md text-light-text-secondary dark:text-gray-400">
          {(loading || initialLoading) ? (
            <div className="flex items-center space-x-1">
              <span>Showing</span>
              <span className="inline-block w-10 h-5 bg-light-surfaceSecondary dark:bg-gray-700 rounded animate-pulse" />
              <span>notifications</span>
            </div>
          ) : (
            <>
              <div className="flex items-center space-x-1">
                <span>Showing</span>
                <span className="font-semibold text-light-text-primary dark:text-white">
                  {showOnlyUnread ? stats.unreadCount : stats.totalNotifications}
                </span>
                <span>notifications</span>
              </div>
              {filterType !== "all" && (
                <>
                  <span>•</span>
                  <span>
                    {filterOptions.find((opt) => opt.value === filterType)?.label}
                  </span>
                </>
              )}
            </>
          )}
        </div>
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <section
            className="py-6 overflow-y-auto"
          >
            {initialLoading || (loading && filteredNotifications.length === 0) ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, idx) => (
                  <MessageSkeleton key={idx} />
                ))}
              </div>
            ) : !loading && !initialLoading && notificationData.length > 0 && filteredNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="flex items-center justify-center w-16 h-16 bg-light-surfaceSecondary dark:bg-gray-800 rounded-full mb-6">
                  <HiBell className="w-8 h-8 text-light-text-tertiary dark:text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-light-text-primary dark:text-white mb-2">
                  No notifications found
                </h3>
                <p className="text-light-text-secondary dark:text-gray-400 text-center max-w-md">
                  {showOnlyUnread
                    ? "You're all caught up! No unread notifications to show."
                    : filterType === "all"
                    ? "You don't have any notifications yet."
                    : "No notifications found for the selected time period."}
                </p>
              </div>
            ) : !loading && !initialLoading && filteredNotifications.length > 0 ? (
              <div className="space-y-4">
                {filteredNotifications.slice(0, visibleCount).map((data: any, index: number) => (
                  <div
                    key={`${data.id}-${index}`}
                    data-notification-index={index}
                    ref={(el) => {
                      if (el) notificationRefs.current[index] = el;
                    }}
                  >
                    <Message
                      key={`${data.id}-${index}`}
                      data={data}
                      index={index}
                      handleClick={handleClick}
                      handleDeleteClick={handleDeleteClick}
                    />
                  </div>
                ))}
                {/* Infinite scroll loader trigger */}
                {visibleCount < filteredNotifications.length && (
                  <div ref={loadMoreRef} className="flex w-full justify-center py-4">
                    <MessageSkeleton />
                  </div>
                )}
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
};

export default Activity;